import React, { useEffect, useState } from "react";
import * as api from "./api.js";
import { supabase } from "./supabaseClient.js";

const FIELD_AR = { name: "اسم الكتاب", nameFr: "الاسم الأجنبي", translator: "المترجم", status: "الحالة", price: "السعر", notes: "ملاحظات", pages: "الصفحات", deadline: "الموعد", lang: "اللغة", publisher: "الناشر", editor: "المحرر", reviewer: "المراجع", authorName: "المؤلف", category: "التصنيف", icon: "الأيقونة", dotColor: "لون الشارة", visibility: "الظهور", transReceived: "استلام الترجمة", approvalStatus: "حالة الاعتماد", finance: "المالية", deliveries: "التسليمات" };

export default function ApprovalsPanel({ onClose }) {
  const [reqs, setReqs] = useState([]);
  const [books, setBooks] = useState({});
  const [members, setMembers] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [fullBooks, setFullBooks] = useState({});
  const [notes, setNotes] = useState({});
  const [history, setHistory] = useState([]);
  const [histDrafts, setHistDrafts] = useState({});
  const [showHist, setShowHist] = useState(false);

  const loadFull = async (r) => {
    if (fullBooks[r.book_id]) return;
    try {
      const { data } = await supabase.from("rel_books").select("*").eq("id", r.book_id).maybeSingle();
      const merged = data ? { ...(data.data || {}), ...data } : {};
      delete merged.data;
      setFullBooks((f) => ({ ...f, [r.book_id]: merged }));
    } catch { setFullBooks((f) => ({ ...f, [r.book_id]: {} })); }
  };

  const diffOf = (r) => {
    const d = drafts[r.draft_id]; const cur = fullBooks[r.book_id];
    if (!d || !d.data) return [];
    if (!cur) return null;
    const skip = new Set(["id", "finance", "deliveries", "updated_at", "updated_by", "created_at", "org_id", "project_id", "projId", "data"]);
    const norm = (x) => (x === null || x === undefined) ? "" : (typeof x === "object" ? JSON.stringify(x) : String(x));
    const rows = [];
    const keyList = (Array.isArray(d.changed_keys) && d.changed_keys.length) ? d.changed_keys.filter((k) => k !== "finance" && k !== "deliveries") : Object.keys(d.data);
    for (const k of keyList) {
      if (skip.has(k)) continue;
      if (norm(d.data[k]) !== norm(cur[k])) rows.push({ k: FIELD_AR[k] || k, before: norm(cur[k]) || "(فارغ)", after: norm(d.data[k]) || "(فارغ)" });
    }
    if (Array.isArray(d.changed_keys) && d.changed_keys.length ? d.changed_keys.includes("finance") : (d.data.finance && norm(d.data.finance) !== norm(cur.finance))) rows.push({ k: "البيانات المالية", before: "(الحالية)", after: "(محدثة - تفاصيلها بعد الاعتماد)" });
    if (Array.isArray(d.changed_keys) && d.changed_keys.length ? d.changed_keys.includes("deliveries") : (d.data.deliveries && norm(d.data.deliveries) !== norm(cur.deliveries))) rows.push({ k: "التسليمات", before: "(الحالية)", after: "(محدثة - تفاصيلها بعد الاعتماد)" });
    return rows;
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const rs = await api.listApprovalRequests(true);
      rs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setReqs(rs);
      try {
        const { data: hist } = await supabase.from("approval_requests").select("*").in("status", ["approved", "rejected"]).order("created_at", { ascending: false }).limit(30);
        setHistory(hist || []);
        const ids = (hist || []).map((h) => h.draft_id).filter(Boolean);
        if (ids.length) {
          const { data: ds } = await supabase.from("book_edit_drafts").select("id, changed_keys").in("id", ids);
          const dm = {}; (ds || []).forEach((d) => { dm[d.id] = d; });
          setHistDrafts(dm);
        }
      } catch {}
      const sc = await api.listScopes();
      const bm = {}; sc.books.forEach((b) => { bm[b.id] = b; }); setBooks(bm);
      const ms = await api.listMembers();
      const mm = {}; ms.forEach((m) => { mm[m.id] = m.full_name || m.email; }); setMembers(mm);
      const dm = {};
      for (const r of rs) if (r.kind === "book_edit" && r.draft_id) { try { dm[r.draft_id] = await api.getDraft(r.draft_id); } catch {} }
      setDrafts(dm);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (r, approve) => {
    setBusy(r.id);
    try {
      const nt = (notes[r.id] || "").trim();
      if (nt) await supabase.from("approval_requests").update({ admin_note: nt }).eq("id", r.id);
    } catch {}
    const res = await api.resolveApproval(r.id, approve);
    setBusy(null);
    if (res.error) { setErr(res.error); return; }
    try { if (window.__tpmsRefresh) window.__tpmsRefresh(); } catch {}
    load();
  };
  const bookPath = (id) => { const b = books[id]; return b ? ((b.project_name ? b.project_name + " / " : "") + (b.name || "بلا اسم")) : "-"; };

  return (
    <div className="ag-modal-bg u4-bg" onClick={onClose}>
      <style>{`
        .ap { font-family: 'Tajawal','Cairo',system-ui,sans-serif; --ap-hl: color-mix(in srgb, var(--accent) 8%, transparent); }
        @keyframes apUp { from { opacity: 0; } to { opacity: 1; } }
        @keyframes apDraw { from { stroke-dashoffset: 20; } to { stroke-dashoffset: 0; } }
        @keyframes apPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); } }
        @keyframes apShake { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-6deg); } 75% { transform: rotate(6deg); } }
        @keyframes apSlide { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
        .ap-f { animation: apUp .18s ease-out both; }
        .ap-header { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
        .ap-title-mark { width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid var(--accent); color: var(--accent); transition: transform .3s cubic-bezier(.34,1.56,.64,1); }
        .ap-title-mark:hover { transform: scale(1.08) rotate(-6deg); box-shadow: 0 0 12px color-mix(in srgb, var(--accent) 40%, transparent); }
        .ap-count-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 22px; padding: 0 7px; border: 1px solid var(--accent); color: var(--accent); background: color-mix(in srgb, var(--accent) 10%, transparent); font-size: 12px; font-weight: 900; }
        .ap-close { font-family: inherit; font-size: 12.5px; font-weight: 700; padding: 6px 14px; border: 1px solid var(--line); background: transparent; color: var(--text); cursor: pointer; transition: border-color .2s, color .2s; }
        .ap-close:hover { border-color: var(--accent); color: var(--accent); }
        .ap-err { border: 1px solid #e23d3d; background: color-mix(in srgb, #e23d3d 6%, transparent); color: #e23d3d; padding: 10px 14px; margin-bottom: 12px; font-size: 13px; }
        .ap-hint { padding: 40px 20px; text-align: center; color: var(--faint); font-size: 13.5px; border: 1px dashed var(--line); background: var(--surface); }
        .ap-card { border: 1px solid var(--line); background: var(--surface); padding: 14px 16px; margin-bottom: 10px; animation: apUp .18s ease-out both; transition: border-color .2s; }
        .ap-card:hover { border-color: color-mix(in srgb, var(--accent) 50%, var(--line)); }
        .ap-card.open { border-color: var(--accent); }
        .ap-card-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .ap-kind-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border: 1px solid var(--accent); color: var(--accent); flex-shrink: 0; }
        .ap-book-title { font-size: 14.5px; font-weight: 900; color: var(--text); }
        .ap-book-path { font-size: 11.5px; color: var(--faint); margin-top: 2px; }
        .ap-meta { display: flex; gap: 12px; margin-inline-start: auto; align-items: center; }
        .ap-meta-item { display: flex; flex-direction: column; align-items: flex-end; font-size: 11px; }
        .ap-meta-label { color: var(--faint); letter-spacing: .04em; }
        .ap-meta-value { color: var(--text); font-weight: 700; font-size: 12.5px; margin-top: 1px; }
        .ap-card-body { margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--line); }
        .ap-note-input { width: 100%; box-sizing: border-box; font-family: inherit; font-size: 13px; padding: 8px 12px; border: 1px solid var(--line); background: color-mix(in srgb, var(--surface) 70%, transparent); color: var(--text); border-radius: 0; transition: border-color .2s, box-shadow .2s; margin-bottom: 10px; }
        .ap-note-input:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 14%, transparent); }
        .ap-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .ap-btn { display: inline-flex; align-items: center; gap: 6px; font-family: inherit; font-size: 12.5px; font-weight: 800; padding: 8px 16px; border-radius: 0; border: 1px solid var(--line); background: transparent; color: var(--text); cursor: pointer; transition: all .18s cubic-bezier(.2,.8,.2,1); }
        .ap-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); transform: translateY(-1px); }
        .ap-btn:disabled { opacity: .5; cursor: not-allowed; }
        .ap-btn.approve { border-color: color-mix(in srgb, #10b981 40%, var(--line)); color: #10b981; }
        .ap-btn.approve:hover:not(:disabled) { border-color: #10b981; background: color-mix(in srgb, #10b981 10%, transparent); }
        .ap-btn.approve:hover:not(:disabled) .ap-check { animation: apDraw .4s cubic-bezier(.65,0,.35,1) forwards; }
        .ap-btn.reject { border-color: color-mix(in srgb, #e23d3d 30%, var(--line)); color: #c22b4e; }
        .ap-btn.reject:hover:not(:disabled) { border-color: #e23d3d; color: #e23d3d; background: color-mix(in srgb, #e23d3d 8%, transparent); }
        .ap-btn.reject:hover:not(:disabled) svg { animation: apPulse .5s ease-in-out; }
        .ap-btn.detail:hover:not(:disabled) svg { animation: apShake .35s ease-in-out; }
        .ap-diff { margin-top: 12px; padding: 10px 14px; background: color-mix(in srgb, var(--accent) 4%, transparent); border: 1px dashed var(--accent); animation: apSlide .2s ease-out both; }
        .ap-diff-title { font-size: 12px; font-weight: 800; color: var(--accent); margin-bottom: 8px; }
        .ap-diff-table { width: 100%; font-size: 12.5px; border-collapse: collapse; }
        .ap-diff-table th { text-align: right; padding: 5px 8px; color: var(--faint); font-size: 11px; font-weight: 800; letter-spacing: .05em; }
        .ap-diff-table td { padding: 6px 8px; border-top: 1px dashed var(--line); }
        .ap-diff-field { font-weight: 800; color: var(--text); }
        .ap-diff-before { color: #e23d3d; text-decoration: line-through; opacity: .8; }
        .ap-diff-after { color: #10b981; font-weight: 800; }
        .ap-section-toggle { display: flex; align-items: center; gap: 8px; margin: 24px 0 12px; padding: 8px 12px; border: 1px solid var(--line); background: var(--surface); cursor: pointer; transition: all .2s; font-family: inherit; width: 100%; text-align: right; font-size: 13.5px; font-weight: 800; color: var(--text); }
        .ap-section-toggle:hover { border-color: var(--accent); color: var(--accent); }
        .ap-section-toggle .ap-chev { margin-inline-start: auto; transition: transform .3s cubic-bezier(.34,1.56,.64,1); }
        .ap-section-toggle.on .ap-chev { transform: rotate(180deg); }
        .ap-hist-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--line); background: var(--surface); margin-bottom: 6px; font-size: 12.5px; animation: apUp .18s ease-out both; }
        .ap-hist-book { font-weight: 800; color: var(--text); flex: 1; min-width: 0; }
        .ap-hist-meta { color: var(--faint); font-size: 11.5px; }
        .ap-status-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; font-size: 11px; font-weight: 800; letter-spacing: .04em; border: 1px solid currentColor; }
        .ap-status-badge.approved { color: #10b981; background: color-mix(in srgb, #10b981 8%, transparent); }
        .ap-status-badge.rejected { color: #e23d3d; background: color-mix(in srgb, #e23d3d 8%, transparent); }
        .ap-foot { margin-top: 14px; padding: 10px 14px; border: 1px dashed var(--line); background: var(--surface); font-size: 12px; color: var(--faint); line-height: 1.6; }
      `}</style>
      <div className="ag-modal wide u4 ap u4-win" onClick={(e) => e.stopPropagation()}>
        <div className="ap-header">
          <span className="ap-title-mark">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          </span>
          <span style={{ fontSize: 19, fontWeight: 900, color: "var(--text)" }}>طلبات الاعتماد</span>
          {reqs.length > 0 && <span className="ap-count-badge">{reqs.length}</span>}
          <button className="ap-close" style={{ marginInlineStart: "auto" }} onClick={onClose}>إغلاق</button>
        </div>
        {err && <div className="ap-err">{err}</div>}
        {loading ? <div className="ap-hint" style={{ marginTop: 12 }}>جارٍ التحميل…</div> : reqs.length === 0 ? (
          <div className="ap-hint">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .5, marginBottom: 8 }}><path d="M20 6L9 17l-5-5"/></svg>
            <div>لا طلبات اعتماد جديدة</div>
          </div>
        ) : (
          <div>
            {reqs.map((r) => (
              <div key={r.id} className={"ap-card " + (openId === r.id ? "open" : "")}>
                <div className="ap-card-head">
                  <span className="ap-kind-icon">
                    {r.kind === "book_add" ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M12 7v6M9 10h6"/></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    )}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ap-book-title">{r.kind === "book_add" ? "كتاب جديد" : "تعديل بيانات كتاب"}</div>
                    <div className="ap-book-path">{bookPath(r.book_id)}{r.kind === "book_edit" && drafts[r.draft_id] ? " · " + Object.keys(drafts[r.draft_id].data || {}).length + " حقول معدّلة" : ""}</div>
                  </div>
                  <div className="ap-meta">
                    <div className="ap-meta-item">
                      <span className="ap-meta-label">مقدّم الطلب</span>
                      <span className="ap-meta-value">{members[r.requested_by] || "-"}</span>
                    </div>
                    <div className="ap-meta-item">
                      <span className="ap-meta-label">التاريخ</span>
                      <span className="ap-meta-value">{new Date(r.created_at).toLocaleDateString("ar", { day: "numeric", month: "short" })}</span>
                    </div>
                  </div>
                </div>
                <div className="ap-card-body">
                  <input value={notes[r.id] || ""} onChange={(ev) => setNotes({ ...notes, [r.id]: ev.target.value })} placeholder="ملاحظة للمعدل (اختيارية)…" className="ap-note-input" />
                  <div className="ap-actions">
                    <button className="ap-btn approve" disabled={busy === r.id} onClick={() => act(r, true)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path className="ap-check" d="M20 6L9 17l-5-5"/></svg>
                      <span>{busy === r.id ? "…" : "اعتماد"}</span>
                    </button>
                    <button className="ap-btn reject" disabled={busy === r.id} onClick={() => act(r, false)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      <span>{busy === r.id ? "…" : "رفض"}</span>
                    </button>
                    {r.kind === "book_edit" && (
                      <button className="ap-btn detail" onClick={() => { if (openId !== r.id) loadFull(r); setOpenId(openId === r.id ? null : r.id); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 10v6m8-11h-6m-4 0H2"/></svg>
                        <span>{openId === r.id ? "إخفاء" : "التفاصيل"}</span>
                      </button>
                    )}
                  </div>
                  {openId === r.id && r.kind === "book_edit" && (() => {
                    const diffs = diffOf(r);
                    if (diffs === null) return <div className="ap-diff" style={{ color: "var(--faint)" }}>جار تحميل الكتاب للمقارنة…</div>;
                    if (diffs.length === 0) return <div className="ap-diff" style={{ color: "var(--faint)" }}>لا فروق ظاهرة في الحقول الأساسية.</div>;
                    return (
                      <div className="ap-diff">
                        <div className="ap-diff-title">الفروقات ({diffs.length} حقلاً)</div>
                        <table className="ap-diff-table">
                          <thead><tr><th>الحقل</th><th>قبل</th><th>بعد التعديل</th></tr></thead>
                          <tbody>
                            {diffs.map((d, i) => (
                              <tr key={i}>
                                <td className="ap-diff-field">{d.k}</td>
                                <td className="ap-diff-before">{d.before}</td>
                                <td className="ap-diff-after">{d.after}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
        {history.length > 0 && (
          <>
            <button className={"ap-section-toggle " + (showHist ? "on" : "")} onClick={() => setShowHist(!showHist)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
              <span>سجل القرارات - آخر {history.length} طلبًا</span>
              <svg className="ap-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {showHist && (
              <div>
                {history.map((h) => {
                  const ck = (histDrafts[h.draft_id] && Array.isArray(histDrafts[h.draft_id].changed_keys)) ? histDrafts[h.draft_id].changed_keys : [];
                  const changes = h.kind === "book_add" ? "كتاب جديد" : (ck.length ? ck.map((k) => FIELD_AR[k] || k).join("، ") : "تعديل بيانات");
                  return (
                    <div key={h.id} className="ap-hist-item">
                      <span className={"ap-status-badge " + h.status}>
                        {h.status === "approved" ? (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        ) : (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        )}
                        <span>{h.status === "approved" ? "اعتماد" : "رفض"}</span>
                      </span>
                      <div className="ap-hist-book">
                        {bookPath(h.book_id)}
                        <div className="ap-hist-meta">{changes} · {members[h.requested_by] || "-"} · {new Date(h.created_at).toLocaleString("ar", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                      {h.admin_note && <div style={{ fontSize: 11.5, color: "var(--faint)", maxWidth: 220, textAlign: "start" }}>«{h.admin_note}»</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        <div className="ap-foot">اعتماد كتاب جديد يثبّته ويتيح منحه للأعضاء. رفض التعديل يمحو المسودة ويبقي الأصل. الكتاب المرفوض يبقى معلقًا حتى يُمسح يدويًا.</div>
      </div>
    </div>
  );
}
